import { LockOpen as LockOpenIcon } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { AxiosError } from 'axios';
import { findIndex, split } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { payoutOrdersApi } from 'api';
import {
  AmountErrorDialogData,
  ConfirmButton,
  DataGrid,
  DataGridColumnDefinition,
  SidebarLayout,
  SidebarSize,
} from 'components';
import { OrdersTab, PayoutOrderStatus, QueryKey, StatusCode } from 'enums';
import { useMutation, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  PayoutOrder,
  PayoutOrderDispute,
  PayoutOrderReject,
  ResolvePayoutOrder,
  ResolvePayoutOrderDispute,
} from 'types';

import { ConfirmPaymentDialog } from '../ConfirmPaymentDialog';
import { PayoutOrderDetails } from '../PayoutOrderDetails';

type Props = {
  items: PayoutOrder[];
  columns: DataGridColumnDefinition<PayoutOrder>[];
  selectedOrder: PayoutOrder | null;
  tab: OrdersTab;
  queryKey: string;
  handleOrderSelection: (orderId: string | null) => void;
  handleAmountErrorDialogOpen: (data: AmountErrorDialogData) => void;
};

export const OrderListWithAction: React.FC<Props> = ({
  items,
  columns,
  selectedOrder,
  tab,
  queryKey,
  handleOrderSelection,
  handleAmountErrorDialogOpen,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders',
  });

  const { role, isManager } = useUser();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const queryClient = useQueryClient();

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries(queryKey);
    queryClient.invalidateQueries(QueryKey.Notifications);
  }, [queryClient, queryKey]);

  const handleListUpdated = useCallback(
    (orderId: string | null) => {
      handleOrderSelection(orderId);
      invalidateQueries();
    },
    [handleOrderSelection, invalidateQueries],
  );

  const { mutate: acceptOrder } = useMutation(payoutOrdersApi.acceptAsTrader, {
    onSuccess: (data) => {
      handleListUpdated(data?.id);
    },
    notifierMessages: {
      error: (error: AxiosError<any>) => {
        const status = error?.response?.status;
        if (status === StatusCode.BadRequest) {
          const message = error.response?.data.message;
          const reason = split(
            message,
            'Unable to accept by the reason: ',
          )?.[1];
          if (reason) {
            invalidateQueries();

            return t(`accept_error.${reason}`, {
              defaultValue: reason,
            });
          }
        }
      },
    },
    notifierType: 'execute',
  });

  const { mutate: rejectOrder } = useMutation(payoutOrdersApi.rejectAsTrader, {
    onSuccess: () => {
      handleListUpdated(null);
    },
    onAmountError: handleAmountErrorDialogOpen,
  });

  const { mutate: disputeOrder } = useMutation(
    payoutOrdersApi.disputeAsRole(role),
    {
      onSuccess: (data) => {
        handleListUpdated(data?.id);
      },
      onAmountError: handleAmountErrorDialogOpen,
    },
  );

  const { mutate: cancelCompleted } = useMutation(
    payoutOrdersApi.cancelCompletedAsRole(role),
    {
      onSuccess: (data) => {
        handleListUpdated(data?.id);
      },
      onAmountError: handleAmountErrorDialogOpen,
    },
  );

  const { mutate: releaseHold, isLoading: releaseHoldLoading } = useMutation(
    payoutOrdersApi.releaseHoldAsRole(role),
    {
      onSuccess: () => {
        handleListUpdated(null);
      },
      onAmountError: handleAmountErrorDialogOpen,
    },
  );

  const { mutate: unlockOrder } = useMutation(
    payoutOrdersApi.unlockAsRole(role),
    {
      onSuccess: (data) => {
        handleListUpdated(data?.id);
      },
      notifierType: 'execute',
    },
  );

  const { mutate: resolveDispute } = useMutation(
    payoutOrdersApi.resolveDisputeAsRole(role),
    {
      onSuccess: (data) => {
        handleListUpdated(data?.id);
      },
      onAmountError: handleAmountErrorDialogOpen,
    },
  );

  const { mutate: resolveOrder } = useMutation(
    payoutOrdersApi.resolveAsRole(role),
    {
      onSuccess: (data) => {
        handleListUpdated(data?.id);
      },
      onAmountError: handleAmountErrorDialogOpen,
    },
  );

  const { mutate: nextAssignee } = useMutation(
    payoutOrdersApi.nextAssigneeAsRole(role),
    {
      onSuccess: (data) => {
        handleListUpdated(data?.id);
      },
    },
  );

  const { mutate: requeryProviders } = useMutation(
    payoutOrdersApi.requeryProvidersAsRole(role),
    {
      onSuccess: (data) => {
        handleListUpdated(data?.id);
      },
      notifierType: 'execute',
      mergeNotifierMessages: {
        success: (data: PayoutOrder) =>
          data?.p2pProviderOrderId
            ? t('requery_result.assigned')
            : t('requery_result.no_provider'),
      },
    },
  );

  const handleAccept = useCallback(() => {
    if (selectedOrder) {
      acceptOrder(selectedOrder);
    }
  }, [acceptOrder, selectedOrder]);

  const handleDispute = useCallback(
    (data: PayoutOrderDispute) => {
      if (selectedOrder) {
        disputeOrder({ id: selectedOrder.id, data });
      }
    },
    [disputeOrder, selectedOrder],
  );

  const handleConfirm = useCallback(() => {
    setConfirmDialogOpen(true);
  }, []);

  const handleReject = useCallback(
    (data: PayoutOrderReject) => {
      if (selectedOrder) {
        rejectOrder({ id: selectedOrder.id, data });
      }
    },
    [rejectOrder, selectedOrder],
  );

  const handleClose = useCallback(() => {
    handleOrderSelection(null);
  }, [handleOrderSelection]);

  const handleCloseConfirmPaymentDialog = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  const handleResolveDispute = useCallback(
    (data: ResolvePayoutOrderDispute) => {
      if (selectedOrder) {
        resolveDispute({ id: selectedOrder.id, data });
      }
    },
    [resolveDispute, selectedOrder],
  );

  const handleResolveOrder = useCallback(
    (data: ResolvePayoutOrder) => {
      if (selectedOrder) {
        resolveOrder({ id: selectedOrder.id, data });
      }
    },
    [resolveOrder, selectedOrder],
  );

  const handleCancelCompleted = useCallback(() => {
    if (selectedOrder) {
      cancelCompleted(selectedOrder.id);
    }
  }, [cancelCompleted, selectedOrder]);

  const handleReleaseHold = useCallback(
    (orderId?: string) => {
      if (orderId) {
        releaseHold(orderId);
      }
    },
    [releaseHold],
  );

  const handleReleaseSelectedHold = useCallback(() => {
    handleReleaseHold(selectedOrder?.id);
  }, [handleReleaseHold, selectedOrder]);

  const handleUnlock = useCallback(() => {
    if (selectedOrder) {
      unlockOrder(selectedOrder.id);
    }
  }, [unlockOrder, selectedOrder]);

  const handleNextAssignee = useCallback(() => {
    if (selectedOrder) {
      nextAssignee(selectedOrder.id);
    }
  }, [nextAssignee, selectedOrder]);

  const handleRequeryProviders = useCallback(() => {
    if (selectedOrder) {
      requeryProviders(selectedOrder.id);
    }
  }, [requeryProviders, selectedOrder]);

  const columnsWithActions = useMemo(() => {
    if (!isManager || tab !== OrdersTab.HoldCompleted) {
      return columns;
    }

    const actionsColumn: DataGridColumnDefinition<PayoutOrder> = {
      header: t('table.columns.actions'),
      valueClassName: 'tw-w-[80px]',
      valueGetter: (order) =>
        order.status === PayoutOrderStatus.Completed &&
        order.holdTraderAsset ? (
          <ConfirmButton
            disabled={releaseHoldLoading}
            onConfirm={() => handleReleaseHold(order.id)}
          >
            {({ onClick }) => (
              <Tooltip title={t('buttons.release_hold')}>
                <span>
                  <IconButton
                    color="primary"
                    disabled={releaseHoldLoading}
                    size="small"
                    onClick={onClick}
                  >
                    <LockOpenIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </ConfirmButton>
        ) : null,
    };

    return [...columns, actionsColumn];
  }, [columns, handleReleaseHold, isManager, releaseHoldLoading, t, tab]);

  return (
    <SidebarLayout
      sidebarProps={{
        open: !!selectedOrder,
        title: t('details.title'),
        size: SidebarSize.md,
        onClose: handleClose,
        content: selectedOrder && (
          <PayoutOrderDetails
            order={selectedOrder}
            onReject={handleReject}
            onDispute={handleDispute}
            onCancelCompleted={handleCancelCompleted}
            onResolveDispute={handleResolveDispute}
            onResolveOrder={handleResolveOrder}
            onConfirm={handleConfirm}
            onAccept={handleAccept}
            onReleaseHold={handleReleaseSelectedHold}
            onUnlock={handleUnlock}
            onNextAssignee={handleNextAssignee}
            onRequeryProviders={handleRequeryProviders}
          />
        ),
      }}
    >
      <DataGrid
        columns={columnsWithActions}
        data={items}
        onRowClick={(order) => handleOrderSelection(order?.id)}
        selectedRowIndex={findIndex(items, {
          id: selectedOrder?.id,
        })}
      />
      <ConfirmPaymentDialog
        open={confirmDialogOpen}
        order={selectedOrder}
        onClose={handleCloseConfirmPaymentDialog}
      />
    </SidebarLayout>
  );
};
